import { useId, useRef, useState } from 'react';
import { PHOTO_TYPES } from '../lib/photos';
import { StoreError } from '../store/errors';
import { useStore } from '../store/StoreProvider';
import { useCtx } from '../store/useCtx';
import { Avatar } from './bits';
import { useDialogs } from './dialogs';
import { useToast } from './Toasts';

export function PhotoEditor({ userId, onBusyChange }: { userId: string; onBusyChange?: (busy: boolean) => void }) {
  const ctx = useCtx();
  const store = useStore();
  const toast = useToast();
  const { confirm } = useDialogs();
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const working = useRef(false);
  const person = ctx.userById.get(userId);

  const save = async (photo: File | null) => {
    if (working.current) return;
    working.current = true;
    setBusy(true);
    onBusyChange?.(true);
    setError('');
    try {
      await store.savePhoto(userId, photo);
      toast(photo ? 'Profile photo updated.' : 'Profile photo removed.');
    } catch (err) {
      console.error(err);
      setError(err instanceof StoreError ? err.message : 'The photo could not be saved. Check your connection and try again.');
    } finally {
      working.current = false;
      setBusy(false);
      onBusyChange?.(false);
    }
  };

  if (!person) return <p role="alert">That person could not be found.</p>;
  const allowed = ctx.me.role === 'hr' || ctx.me.id === userId;
  if (!allowed) return null;

  return (
    <section className="photo-editor" aria-label="Profile photo" aria-busy={busy}>
      <div className="employee-identity">
        <Avatar name={person.name} photoUrl={person.photoUrl} className="avatar-lg" />
        <div className="photo-fields">
          <label htmlFor={id}>{person.photoUrl ? 'Change photo' : 'Add photo'}</label>
          <input
            id={id} type="file" accept={PHOTO_TYPES.join(',')} disabled={busy}
            aria-describedby={`${id}-help${error ? ` ${id}-error` : ''}`}
            onChange={(e) => {
              const photo = e.currentTarget.files?.[0];
              e.currentTarget.value = '';
              if (photo) void save(photo);
            }}
          />
        </div>
      </div>
      <p className="hint" id={`${id}-help`}>JPEG, PNG or WebP, up to 5 MiB and 16 megapixels. Photos save immediately.</p>
      {busy && <p role="status">Saving photo...</p>}
      {error && <p className="text-warn" id={`${id}-error`} role="alert">{error}</p>}
      {person.photoUrl && (
        <button type="button" className="btn btn-ghost-danger btn-sm" disabled={busy} onClick={async () => {
          if (await confirm({
            title: 'Remove profile photo?', message: 'Their initials will be shown instead.',
            confirmLabel: 'Remove photo', danger: true,
          })) await save(null);
        }}>Remove photo</button>
      )}
    </section>
  );
}
